import React, { useEffect, useState } from 'react';
import SearchableSelect from '../common/SearchableSelect';
import { SalesCatalogueService } from '../../services/salesCatalogue.service';
import { SalesCatalogueItem } from '../../interfaces/sales/salesCatalogue.interface';
import { helpElement } from '../../help/attrs';

interface Props {
  value: string | null;
  /** Label of the current value when it is not in the first page of results (an edited line). */
  valueLabel?: string;
  onChange: (id: string, item: SalesCatalogueItem | null) => void;
  label?: string;
  size?: 'small' | 'medium';
  sx?: object;
  kind?: 'STOCK' | 'SERVICE' | 'KIT';   // F6c: KIT
  /** Help-mode key, put on the picker's root so a form can explain it. */
  'data-help'?: string;
}

/**
 * FW-2A: picker over the sellable products and services (server search, SearchableSelect). Open to any role that can see
 * invoices, quotations or orders, unlike the inventory item list. Picking an item is up to the caller: it usually fills
 * description, unit price, revenue account and tax code, all of which stay editable.
 */
const SalesCatalogueSelect: React.FC<Props> = ({ value, valueLabel, onChange, label = 'Product / service', size = 'small', sx, kind, ...rest }) => {
  const [options, setOptions] = useState<SalesCatalogueItem[]>([]);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let live = true;
    setLoading(true);
    const t = setTimeout(() => {
      SalesCatalogueService.search(text, kind, 0, 30)
        .then(r => { if (live) setOptions(r.content ?? []); })
        .catch(() => { if (live) setOptions([]); })
        .finally(() => { if (live) setLoading(false); });
    }, 250);
    return () => { live = false; clearTimeout(t); };
  }, [text, kind]);

  const selectedOption: SalesCatalogueItem | undefined = value && valueLabel
    ? { id: value, code: valueLabel, name: '', kind: 'STOCK' }
    : undefined;

  return (
    <SearchableSelect<SalesCatalogueItem>
      label={label} size={size} sx={sx} options={options} value={value} selectedOption={selectedOption} clearable
      {...helpElement(rest['data-help'])}
      loading={loading} disableClientFilter onInputChange={setText}
      getOptionLabel={i => (i.name ? `${i.code} - ${i.name}` : i.code)} getOptionId={i => i.id}
      onChange={(id, opt) => onChange(id, opt)}
    />
  );
};

export default SalesCatalogueSelect;
